import type {
  FloorPlan,
  CreateFloorPlanInput,
  UpdateFloorPlanInput,
  AssignGuestsToTableInput,
  TableAssignment,
  Room,
  CreateRoomInput,
  UpdateRoomInput,
  CreateRoomEntryInput,
  RoomEntry,
  FloorPlanObject,
  CreateFloorPlanObjectInput,
  UpdateFloorPlanObjectInput,
  BulkUpsertObjectsInput,
  DuplicateFloorPlanInput,
} from '@peles/shared';
import { apiClient } from './client';

// ─── Floor Plans ────────────────────────────────────────────────────────────

export async function getFloorPlans(filters?: {
  eventId?: string;
  venueId?: string;
  isTemplate?: boolean;
}): Promise<FloorPlan[]> {
  const params: Record<string, string> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.venueId) params.venueId = filters.venueId;
  if (filters?.isTemplate) params.isTemplate = 'true';
  const { data } = await apiClient.get<FloorPlan[]>('/floor-plans', {
    params,
  });
  return data;
}

export async function getFloorPlan(id: string): Promise<FloorPlan> {
  const { data } = await apiClient.get<FloorPlan>(`/floor-plans/${id}`);
  return data;
}

export async function createFloorPlan(input: CreateFloorPlanInput): Promise<FloorPlan> {
  const { data } = await apiClient.post<FloorPlan>('/floor-plans', input);
  return data;
}

export async function updateFloorPlan(
  id: string,
  updates: UpdateFloorPlanInput,
): Promise<FloorPlan> {
  const { data } = await apiClient.patch<FloorPlan>(`/floor-plans/${id}`, updates);
  return data;
}

export async function deleteFloorPlan(id: string): Promise<void> {
  await apiClient.delete(`/floor-plans/${id}`);
}

export async function assignGuestsToTable(input: AssignGuestsToTableInput): Promise<void> {
  await apiClient.post('/floor-plans/assign-guests', input);
}

export async function getTableAssignments(
  eventId: string,
): Promise<Record<number, TableAssignment['guests']>> {
  const { data } = await apiClient.get<Record<number, TableAssignment['guests']>>(
    '/floor-plans/assignments',
    { params: { eventId } },
  );
  return data;
}

export async function cloneAsTemplate(id: string, templateName?: string): Promise<FloorPlan> {
  const { data } = await apiClient.post<FloorPlan>(`/floor-plans/${id}/clone-template`, {
    templateName,
  });
  return data;
}

export async function applyTemplate(
  templateId: string,
  eventId: string,
  name?: string,
): Promise<FloorPlan> {
  const { data } = await apiClient.post<FloorPlan>(`/floor-plans/templates/${templateId}/apply`, {
    eventId,
    name,
  });
  return data;
}

// ─── Rooms ──────────────────────────────────────────────────────────────────

export async function getRooms(venueId?: string): Promise<Room[]> {
  const params: Record<string, string> = {};
  if (venueId) params.venueId = venueId;
  const { data } = await apiClient.get<Room[]>('/rooms', { params });
  return data;
}

export async function createRoom(input: CreateRoomInput): Promise<Room> {
  const { data } = await apiClient.post<Room>('/rooms', input);
  return data;
}

export async function updateRoom(id: string, updates: UpdateRoomInput): Promise<Room> {
  const { data } = await apiClient.patch<Room>(`/rooms/${id}`, updates);
  return data;
}

export async function deleteRoom(id: string): Promise<void> {
  await apiClient.delete(`/rooms/${id}`);
}

export async function createRoomEntry(
  roomId: string,
  input: CreateRoomEntryInput,
): Promise<RoomEntry> {
  const { data } = await apiClient.post<RoomEntry>(`/rooms/${roomId}/entries`, input);
  return data;
}

export async function deleteRoomEntry(roomId: string, entryId: string): Promise<void> {
  await apiClient.delete(`/rooms/${roomId}/entries/${entryId}`);
}

// ─── Floor Plan Objects ─────────────────────────────────────────────────────

export async function createFloorPlanObject(
  floorPlanId: string,
  input: CreateFloorPlanObjectInput,
): Promise<FloorPlanObject> {
  const { data } = await apiClient.post<FloorPlanObject>(
    `/floor-plans/${floorPlanId}/objects`,
    input,
  );
  return data;
}

export async function updateFloorPlanObject(
  floorPlanId: string,
  objId: string,
  updates: UpdateFloorPlanObjectInput,
): Promise<FloorPlanObject> {
  const { data } = await apiClient.patch<FloorPlanObject>(
    `/floor-plans/${floorPlanId}/objects/${objId}`,
    updates,
  );
  return data;
}

export async function deleteFloorPlanObject(floorPlanId: string, objId: string): Promise<void> {
  await apiClient.delete(`/floor-plans/${floorPlanId}/objects/${objId}`);
}

export async function bulkUpsertObjects(
  floorPlanId: string,
  input: BulkUpsertObjectsInput,
): Promise<FloorPlanObject[]> {
  const { data } = await apiClient.put<FloorPlanObject[]>(
    `/floor-plans/${floorPlanId}/objects`,
    input,
  );
  return data;
}

// ─── Duplicate ──────────────────────────────────────────────────────────────

export async function duplicateFloorPlan(
  id: string,
  input: DuplicateFloorPlanInput,
): Promise<FloorPlan> {
  const { data } = await apiClient.post<FloorPlan>(`/floor-plans/${id}/duplicate`, input);
  return data;
}

// ─── Share ───────────────────────────────────────────────────────────────────

export async function shareFloorPlan(id: string): Promise<{ shareToken: string; url: string }> {
  const { data } = await apiClient.post<{ shareToken: string; url: string }>(
    `/floor-plans/${id}/share`,
  );
  return data;
}

export async function getPublicFloorPlan(
  token: string,
): Promise<{ floorPlan: FloorPlan; guests: any[] }> {
  const { data } = await apiClient.get<{ floorPlan: FloorPlan; guests: any[] }>(
    `/public/floor-plans/${token}`,
  );
  return data;
}

// ─── Seat Assignment ─────────────────────────────────────────────────────────

export async function assignGuestToSeat(
  guestId: string,
  tableNumber: number,
  seatNumber: number,
): Promise<void> {
  await apiClient.post('/floor-plans/assign-seat', {
    guestId,
    tableNumber,
    seatNumber,
  });
}

// ─── Meal Counts ─────────────────────────────────────────────────────────────

export async function getTableCapacities(eventId: string): Promise<Record<number, number>> {
  const { data } = await apiClient.get<Record<number, number>>('/floor-plans/table-capacities', {
    params: { eventId },
  });
  return data;
}

export async function getTableMealCounts(
  eventId: string,
): Promise<Record<number, Record<string, number>>> {
  const { data } = await apiClient.get<Record<number, Record<string, number>>>(
    '/floor-plans/meal-counts',
    { params: { eventId } },
  );
  return data;
}

// ─── Auto-Assign Seats ────────────────────────────────────────────────────

export interface AutoAssignSeatsRequest {
  strategy: 'BY_GROUP' | 'BY_SIDE' | 'SEPARATE_MF' | 'KIDS_WITH_ADULTS' | 'KIDS_SEPARATE';
  respectKeepTogether?: boolean;
  dryRun?: boolean;
  autoAddTables?: {
    seatsPerTable: number;
    tableType?: 'ROUND' | 'OVAL' | 'SQUARE' | 'RECTANGULAR' | 'HEAD';
  };
}

export interface AutoAssignSeatsResult {
  assignments: Array<{ guestId: string; tableNumber: number; seatNumber: number }>;
  unassigned: Array<{ guestId: string; reason: string }>;
  shortfall: { partySeats: number; tablesNeeded: number };
  tablesAdded: Array<{ id: string; tableNumber: number; seats: number }>;
  applied: boolean;
}

export async function autoAssignSeats(
  floorPlanId: string,
  body: AutoAssignSeatsRequest,
): Promise<AutoAssignSeatsResult> {
  const { data } = await apiClient.post<AutoAssignSeatsResult>(
    `/floor-plans/${floorPlanId}/auto-assign`,
    body,
  );
  return data;
}
